import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, Select, Tag } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';

export type MetadataAutofillFieldKey = 'genre' | 'tropes' | 'keywords' | 'intendedAudience';

export interface MetadataAutofillRun {
  status?: string;
  generatedAt?: string;
  modelUsed?: string;
  suggestedFields?: Record<string, unknown>;
  fieldConfidence?: Record<string, unknown>;
  appliedFields?: string[];
  appliedValues?: Record<string, unknown>;
  manuallyEditedFields?: string[];
}

interface MetadataAutofillFieldDefinition {
  key: MetadataAutofillFieldKey;
  label: string;
  helper: string;
  placeholder: string;
}

const FIELD_DEFINITIONS: MetadataAutofillFieldDefinition[] = [
  {
    key: 'genre',
    label: 'Genres',
    helper: 'Storefront categories and translation positioning.',
    placeholder: 'Type a genre and press Enter',
  },
  {
    key: 'tropes',
    label: 'Tropes',
    helper: 'Reader-facing patterns inferred from the manuscript.',
    placeholder: 'Type a trope and press Enter',
  },
  {
    key: 'keywords',
    label: 'Keywords',
    helper: 'Search and discoverability phrases.',
    placeholder: 'Type a keyword and press Enter',
  },
  {
    key: 'intendedAudience',
    label: 'Intended audiences',
    helper: 'Reader and translation-behavior labels.',
    placeholder: 'Type an audience and press Enter',
  },
];

export const normalizeMetadataAutofillValues = (value: unknown): string[] => {
  const rawValues = Array.isArray(value) ? value : typeof value === 'string' ? [value] : [];
  const normalized: string[] = [];
  const seen = new Set<string>();
  rawValues.forEach(rawValue => {
    String(rawValue || '')
      .split(/[,;\n]+/)
      .map(part => part.trim())
      .filter(Boolean)
      .forEach(part => {
        const key = part.toLocaleLowerCase();
        if (!seen.has(key) && normalized.length < 40) {
          seen.add(key);
          normalized.push(part.slice(0, 80));
        }
      });
  });
  return normalized;
};

const getSuggestedValues = (
  run: MetadataAutofillRun | null | undefined,
  field: MetadataAutofillFieldKey,
): string[] => {
  const suggestions = run?.suggestedFields || {};
  const value = field === 'genre'
    ? suggestions.genre || suggestions.genres
    : suggestions[field];
  return normalizeMetadataAutofillValues(value);
};

export const buildMetadataAutofillDraftValues = (
  run: MetadataAutofillRun | null | undefined,
): Record<MetadataAutofillFieldKey, string[]> => {
  const manuallyEdited = new Set(run?.manuallyEditedFields || []);
  return FIELD_DEFINITIONS.reduce((draft, field) => {
    const savedManualValues = manuallyEdited.has(field.key)
      ? normalizeMetadataAutofillValues(run?.appliedValues?.[field.key])
      : [];
    draft[field.key] = manuallyEdited.has(field.key)
      ? savedManualValues
      : getSuggestedValues(run, field.key);
    return draft;
  }, {} as Record<MetadataAutofillFieldKey, string[]>);
};

const getConfidence = (
  run: MetadataAutofillRun | null | undefined,
  field: MetadataAutofillFieldKey,
): number => {
  const raw = run?.fieldConfidence?.[field]
    ?? (field === 'genre' ? run?.fieldConfidence?.genres : undefined);
  const numeric = Number(raw);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, Math.min(1, numeric > 1 ? numeric / 100 : numeric));
};

interface MetadataAutofillReviewModalProps {
  open: boolean;
  loading: boolean;
  run?: MetadataAutofillRun | null;
  onClose: () => void;
  onApply: (
    fields: MetadataAutofillFieldKey[],
    values: Record<string, string[]>,
  ) => Promise<{ status?: string } | null | undefined>;
  onStartTour?: () => void;
}

const MetadataAutofillReviewModal: React.FC<MetadataAutofillReviewModalProps> = ({
  open,
  loading,
  run,
  onClose,
  onApply,
  onStartTour,
}) => {
  const [draftValues, setDraftValues] = useState<Record<MetadataAutofillFieldKey, string[]>>(
    () => buildMetadataAutofillDraftValues(run),
  );
  const initializedRunRef = useRef<string | null>(null);

  const runKey = `${run?.generatedAt || 'existing'}:${run?.modelUsed || 'unknown'}`;
  useEffect(() => {
    if (!open) {
      initializedRunRef.current = null;
      return;
    }
    if (initializedRunRef.current === runKey) return;
    setDraftValues(buildMetadataAutofillDraftValues(run));
    initializedRunRef.current = runKey;
  }, [open, run, runKey]);

  const appliedFields = useMemo(() => new Set(run?.appliedFields || []), [run?.appliedFields]);
  const manuallyEditedFields = useMemo(
    () => new Set(run?.manuallyEditedFields || []),
    [run?.manuallyEditedFields],
  );
  const applicableFields = FIELD_DEFINITIONS.filter(
    field => (draftValues[field.key] || []).length > 0 || appliedFields.has(field.key),
  );
  const valueCount = Object.values(draftValues).reduce((count, values) => count + values.length, 0);

  const buildPayload = (fields: MetadataAutofillFieldDefinition[]): Record<string, string[]> =>
    fields.reduce<Record<string, string[]>>((payload, field) => {
      payload[field.key] = normalizeMetadataAutofillValues(draftValues[field.key]);
      return payload;
    }, {});

  const applyFields = async (fields: MetadataAutofillFieldDefinition[], closeOnSuccess: boolean) => {
    if (fields.length === 0) return;
    const result = await onApply(fields.map(field => field.key), buildPayload(fields));
    if (closeOnSuccess && result?.status === 'success') {
      onClose();
    }
  };

  return (
    <Modal
      title={(
        <div className="translation-modal-titlebar">
          <span className="translation-modal-titlebar__title">Review book metadata</span>
          {onStartTour && (
            <Button
              type="text"
              size="small"
              icon={<QuestionCircleOutlined />}
              onClick={onStartTour}
              data-tour-id="metadata-autofill-review-tour-trigger"
              className="translation-modal-titlebar__tour app-tour-trigger"
            >
              Take a tour
            </Button>
          )}
        </div>
      )}
      open={open}
      onCancel={onClose}
      width={880}
      centered
      className="metadata-autofill-review-modal"
      destroyOnClose
      footer={[
        <Button key="close" onClick={onClose}>Close</Button>,
        <Button
          key="apply-reviewed"
          type="primary"
          className="app-primary-cta"
          data-tour-id="metadata-autofill-review-apply-selected"
          loading={loading}
          disabled={applicableFields.length === 0}
          onClick={() => void applyFields(applicableFields, true)}
        >
          Apply reviewed metadata
        </Button>,
      ]}
    >
      <div className="metadata-autofill-modal-body">
        <div className="metadata-autofill-modal-summary" data-tour-id="metadata-autofill-review-summary">
          <div>
            <strong>{valueCount} values ready to apply</strong>
            <span>Remove AI suggestions or type your own values below. Your edits are saved as authoritative book metadata.</span>
          </div>
          <div className="metadata-autofill-summary-tags">
            <Tag color="blue">Editable review</Tag>
          </div>
        </div>

        <div className="metadata-autofill-review-list" data-tour-id="metadata-autofill-review-list">
          {FIELD_DEFINITIONS.map((field, index) => {
            const values = draftValues[field.key] || [];
            const confidence = getConfidence(run, field.key);
            const manuallyEdited = manuallyEditedFields.has(field.key);
            const canApply = values.length > 0 || appliedFields.has(field.key);
            return (
              <section
                key={field.key}
                className="metadata-autofill-review-row"
                data-tour-id={index === 0 ? 'metadata-autofill-review-first-row' : undefined}
              >
                <div className="metadata-autofill-review-row-header">
                  <div className="metadata-autofill-review-row-title">
                    <strong>{field.label}</strong>
                    {manuallyEdited ? (
                      <Tag color="blue">Edited by you</Tag>
                    ) : confidence > 0 ? (
                      <Tag color={confidence >= 0.85 ? 'green' : confidence >= 0.80 ? 'gold' : 'orange'}>
                        {Math.round(confidence * 100)}% AI confidence
                      </Tag>
                    ) : (
                      <Tag>Review required</Tag>
                    )}
                  </div>
                  <Button
                    size="small"
                    data-tour-id={index === 0 ? 'metadata-autofill-review-first-apply' : undefined}
                    loading={loading}
                    disabled={!canApply}
                    onClick={() => void applyFields([field], false)}
                  >
                    {values.length > 0 ? `Apply ${field.label.toLocaleLowerCase()}` : `Clear ${field.label.toLocaleLowerCase()}`}
                  </Button>
                </div>
                <div className="metadata-autofill-review-row-helper">
                  {field.helper} Remove suggestions or enter your own; commas create separate values.
                </div>
                <div
                  className="metadata-autofill-tag-editor"
                  data-tour-id={index === 0 ? 'metadata-autofill-review-first-values' : undefined}
                >
                  <Select
                    aria-label={`Edit ${field.label}`}
                    mode="tags"
                    value={values}
                    tokenSeparators={[',', ';']}
                    placeholder={field.placeholder}
                    style={{ width: '100%' }}
                    onChange={nextValues => setDraftValues(previous => ({
                      ...previous,
                      [field.key]: normalizeMetadataAutofillValues(nextValues),
                    }))}
                  >
                    {getSuggestedValues(run, field.key).map(value => (
                      <Select.Option key={value} value={value}>{value}</Select.Option>
                    ))}
                  </Select>
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </Modal>
  );
};

export default MetadataAutofillReviewModal;
